import { useState } from 'react';

const fmtDate = (d) => new Date(d).toLocaleString('es-CO', {
  timeZone: 'America/Bogota', dateStyle: 'medium', timeStyle: 'short'
});

const doctorLabel = (d) =>
  `${d.user?.profile?.firstName || ''} ${d.user?.profile?.lastName || ''}`.trim() || d.user?.email || '—';

/**
 * Reasigna una cita a otro médico, en cualquier estado.
 *
 * Pide el motivo (queda en la auditoría). Solo ofrece médicos verificados
 * distintos del actual; el backend vuelve a validarlo y rechaza el cambio si
 * el nuevo médico tiene ocupado el horario.
 */
export default function ChangeDoctorModal({ appointment, doctors, onClose, onConfirm }) {
  const [doctorId, setDoctorId] = useState('');
  const [reason, setReason]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const currentId = appointment.doctor?._id ?? appointment.doctor;
  const options   = doctors.filter(d => d._id !== currentId && d.isVerified !== false);
  const patient   = appointment.patient?.user;
  const patientName = patient
    ? `${patient.profile?.firstName || ''} ${patient.profile?.lastName || ''}`.trim()
    : '—';
  const payoutPaid = appointment.doctorPayoutStatus === 'paid';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctorId || !reason.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onConfirm(doctorId, reason.trim());
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cambiar el médico');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" role="dialog" aria-modal="true" aria-labelledby="change-doctor-title">
        <div className="modal-header">
          <h2 id="change-doctor-title">Cambiar médico</h2>
          <button className="btn-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <p className="delete-appt-summary">
          {fmtDate(appointment.scheduledDate)} · {patientName}
        </p>

        <div className="alert alert-warning">
          La historia clínica, si existe, sigue a nombre del médico que atendió.
          Si la consulta está completada y sin pagar al médico, pasa al balance del nuevo.
        </div>

        {payoutPaid && (
          <div className="alert alert-error" data-testid="payout-warning">
            Esta consulta ya se le pagó al médico actual. Si la reasignas, ajusta ese pago por fuera.
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="edit-appt-form">
          <label>Nuevo médico
            <select value={doctorId} onChange={e => setDoctorId(e.target.value)} autoFocus>
              <option value="" disabled>-- Elige un médico --</option>
              {options.map(d => (
                <option key={d._id} value={d._id}>
                  {doctorLabel(d)}{d.specialization ? ` • ${d.specialization}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label>Motivo (obligatorio)
            <textarea
              value={reason}
              rows={3}
              maxLength={500}
              onChange={e => setReason(e.target.value)}
              placeholder="Ej.: el médico asignado no puede atender ese día"
            />
          </label>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Volver</button>
            <button type="submit" className="btn-primary" disabled={saving || !doctorId || !reason.trim()}>
              {saving ? 'Guardando...' : 'Cambiar médico'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
